import { useCallback, useMemo, useState } from 'react';
import { UsersIcon } from '@/components/catalog/icons';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Notice } from '@/components/ui/Notice';
import { Skeleton } from '@/components/ui/Skeleton';
import { TBody, TH, THead, TR, Table, TableWrap } from '@/components/ui/Table';
import { Tooltip } from '@/components/ui/Tooltip';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import type { Contact } from '@/lib/types';
import { ContactAddRow } from './ContactAddRow';
import { ContactDeleteDialog } from './ContactDeleteDialog';
import { ContactManagerCard } from './ContactManagerCard';
import { ContactManagerRow } from './ContactManagerRow';
import { INLINE_FIELD_LABELS, orderContactsForManager } from './rules';
import { useCompanyContacts } from './useCompanyContacts';

/**
 * The people at one company: edit any of them in place, add one, delete one,
 * change their group, make one the primary contact, and write to them (each
 * address is a `mailto:` link; the Gmail draft is SUPERSEDED 2026-10-16). Owner, 2026-10-16: this
 * replaces the read-only `CompanyContactsMailModal` behind the row's contacts
 * button on `/companies` and on the University Partnerships dashboard.
 *
 * ## ⛔ MOUNTED ONCE PER PAGE, NEVER ONCE PER ROW
 *
 * `company` is the whole open/closed state: `null` is closed, an object is open
 * on that company. A page holds one piece of state and renders one of these
 * beside its table. One per row would mount twenty hooks each holding an
 * effect, and is how two rows come to show two dialogs.
 *
 * ## ⚠️ THE FETCH IS KEYED ON THE ID, NOT ON THE OBJECT
 *
 * Callers compose `{ id: row.id, name: row.name }` inline, a new object every
 * render. Only the number is handed to `useCompanyContacts`, so a keystroke in
 * the list's search box does not refire `/contacts`.
 *
 * ## ⛔ THE DIALOG ALWAYS OPENS
 *
 * About 30% of companies have no emailable contact and some have none at all.
 * The dialog opens on every row and says so (EmptyState), with "Add contact"
 * right there. Contacts without an address are listed, as plain text in the
 * email cell, never hidden.
 *
 * ## ⚠️ PRIMARY IS A COMPANY WRITE, NOT A CONTACT WRITE
 *
 * Making somebody primary is `PATCH /companies/:id {primaryContactId}` (grant
 * `companies.update`), the same single writer the company page uses. Contact
 * edits, the group, add and delete are `contacts.*`. The two grants differ, so
 * each control is gated on its own; the server's 403 is the real boundary.
 *
 * Declared at module level with every helper above it: a component defined
 * during a render remounts its subtree and would restart the fetch.
 */
export interface CompanyContactsManagerModalProps {
  /** The company whose contacts to manage, or `null` when closed. There is no separate `open`. */
  company: { id: number; name: string } | null;
  onClose: () => void;
  /**
   * Called after any add or delete with the new contact count, so the page can
   * update the row's count badge without refetching its list.
   */
  onChanged?: (companyId: number, contactCount: number) => void;
}

/** Best message an unknown failure can give: form-level, then the error's own. */
function messageOf(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  if (caught instanceof Error && caught.message !== '') return caught.message;
  return fallback;
}

const NO_CREATE_REASON = 'Your permission set does not include adding contacts.';

export function CompanyContactsManagerModal({
  company,
  onClose,
  onChanged,
}: CompanyContactsManagerModalProps) {
  /* ⚠️ THE DEPENDENCY: a number, never the inline-built object. */
  const companyId = company?.id ?? null;
  const { ability } = useAuth();

  const {
    contacts,
    groups,
    primaryContactId,
    loading,
    error,
    truncated,
    replaceContact,
    addContact,
    removeContact,
    setPrimaryContactId,
  } = useCompanyContacts(companyId);

  /*
   * Per-company UI state is keyed on the id it was opened for, so reopening on a
   * different row never shows the previous company's add form or delete dialog.
   */
  const [addingFor, setAddingFor] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<{ companyId: number; contact: Contact } | null>(null);

  const adding = companyId !== null && addingFor === companyId;
  const deletingContact =
    deleting !== null && deleting.companyId === companyId ? deleting.contact : null;

  const canCreate = ability.canCreate('contacts');
  const canEditContact = ability.canUpdate('contacts');
  const canDelete = ability.canDelete('contacts');
  const canEditCompany = ability.canUpdate('companies');

  const ordered = useMemo(
    () => orderContactsForManager(contacts, primaryContactId),
    [contacts, primaryContactId],
  );

  const trimmedName = company?.name.trim() ?? '';
  const shownName = trimmedName === '' ? 'this company' : trimmedName;

  /*
   * Skeletons only when nothing is on screen yet. A reload of the same company
   * keeps its rows visible while the fresh read replaces them.
   */
  const pending = loading && contacts.length === 0;

  const handleCreated = useCallback(
    (contact: Contact) => {
      if (companyId === null) return;
      const already = contacts.some((existing) => existing.id === contact.id);
      addContact(contact);
      setAddingFor(null);
      onChanged?.(companyId, already ? contacts.length : contacts.length + 1);
    },
    [addContact, companyId, contacts, onChanged],
  );

  const handleDeleted = useCallback(
    (id: number) => {
      if (companyId === null) return;
      const present = contacts.some((existing) => existing.id === id);
      removeContact(id);
      setDeleting(null);
      onChanged?.(companyId, present ? contacts.length - 1 : contacts.length);
    },
    [companyId, contacts, onChanged, removeContact],
  );

  /*
   * Rejects on failure: the row (`useContactRowWrites.makePrimary`) shows the
   * refusal beside the control that was pressed, so nothing is repeated here.
   */
  const handleMakePrimary = useCallback(
    async (contactId: number) => {
      if (companyId === null) return;
      const response = await api.updateCompany(companyId, { primaryContactId: contactId });
      setPrimaryContactId(response.data.primaryContact?.id ?? contactId);
    },
    [companyId, setPrimaryContactId],
  );

  const handleDelete = useCallback(
    (contact: Contact) => {
      if (companyId === null) return;
      setDeleting({ companyId, contact });
    },
    [companyId],
  );

  const addButton = (
    <Button
      variant="secondary"
      size="sm"
      disabled={!canCreate || adding || companyId === null}
      onClick={() => setAddingFor(companyId)}
    >
      + Add contact
    </Button>
  );

  return (
    <>
      <Modal
        open={company !== null}
        onClose={onClose}
        title={`Contacts — ${shownName}`}
        size="xl"
        headerActions={
          canCreate ? (
            addButton
          ) : (
            <Tooltip label={NO_CREATE_REASON}>
              <span className="inline-flex">{addButton}</span>
            </Tooltip>
          )
        }
        footer={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          {error !== null && (
            <Notice tone="danger" role="alert">
              {messageOf(error, 'Failed to load contacts.')}
            </Notice>
          )}

          {adding && companyId !== null && (
            <ContactAddRow
              companyId={companyId}
              groups={groups}
              onCreated={handleCreated}
              onCancel={() => setAddingFor(null)}
            />
          )}

          {pending && (
            <ul aria-hidden="true" className="m-0 flex list-none flex-col gap-3 p-0">
              {[0, 1, 2].map((index) => (
                <li key={index} className="flex items-center gap-3">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-56" />
                  <Skeleton className="h-4 w-24" />
                </li>
              ))}
            </ul>
          )}

          {/* A failed read is not "no contacts": the empty answer needs a clean read. */}
          {!loading && error === null && contacts.length === 0 && !adding && (
            <EmptyState
              icon={<UsersIcon className="h-4 w-4" />}
              title="No contacts at this company"
              action={canCreate ? addButton : undefined}
            />
          )}

          {!pending && ordered.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
                  <Table>
                    <THead>
                      <TR>
                        <TH>
                          <span className="sr-only">Primary</span>
                        </TH>
                        <TH>{INLINE_FIELD_LABELS.name}</TH>
                        <TH>{INLINE_FIELD_LABELS.firstName}</TH>
                        <TH>{INLINE_FIELD_LABELS.lastName}</TH>
                        <TH>{INLINE_FIELD_LABELS.title}</TH>
                        <TH>{INLINE_FIELD_LABELS.email}</TH>
                        <TH>{INLINE_FIELD_LABELS.phone}</TH>
                        <TH>{INLINE_FIELD_LABELS.mobile}</TH>
                        <TH>Group</TH>
                        <TH>
                          <span className="sr-only">Actions</span>
                        </TH>
                      </TR>
                    </THead>
                    <TBody>
                      {ordered.map((contact) => (
                        <ContactManagerRow
                          key={contact.id}
                          contact={contact}
                          groups={groups}
                          isPrimary={contact.id === primaryContactId}
                          canEditContact={canEditContact}
                          canEditCompany={canEditCompany}
                          canDelete={canDelete}
                          onSaved={replaceContact}
                          onMakePrimary={handleMakePrimary}
                          onDelete={handleDelete}
                        />
                      ))}
                    </TBody>
                  </Table>
                </TableWrap>
              </div>

              <ul className="m-0 flex list-none flex-col gap-3 p-0 md:hidden">
                {ordered.map((contact) => (
                  <li key={contact.id}>
                    <ContactManagerCard
                      contact={contact}
                      groups={groups}
                      isPrimary={contact.id === primaryContactId}
                      canEditContact={canEditContact}
                      canEditCompany={canEditCompany}
                      canDelete={canDelete}
                      onSaved={replaceContact}
                      onMakePrimary={handleMakePrimary}
                      onDelete={handleDelete}
                    />
                  </li>
                ))}
              </ul>

              {/* A fact about the data: a silently short list hides real people. */}
              {truncated && (
                <p className="m-0 text-xs text-fg-muted">
                  Not all contacts are listed — this shows the first {contacts.length}.
                </p>
              )}
            </>
          )}
        </div>
      </Modal>

      <ContactDeleteDialog
        contact={deletingContact}
        isPrimary={deletingContact !== null && deletingContact.id === primaryContactId}
        companyName={shownName}
        onClose={() => setDeleting(null)}
        onDeleted={handleDeleted}
      />
    </>
  );
}
